import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Stack, 
  useTheme, 
  keyframes,
  Divider,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Drawer,
  Fade
} from '@mui/material';
import { 
  Speed as SpeedIcon, 
  SupportAgent as SupportIcon, 
  Dashboard as AdminIcon,
  People as ClientIcon,
  Engineering as TechIcon,
  Wifi as WifiIcon,
  SettingsInputSvideo as FiberIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  PhoneAndroid as MobileIcon,
  AccountBalanceWallet as WalletIcon,
  Timeline as ChartIcon,
  LiveHelp as FaqIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  Mail as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  Phone as PhoneIcon,
  EmailOutlined as EmailOutlinedIcon,
  AccountBalance as BankIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TermsModal from '../components/legal/TermsModal';

// Animaciones CSS premium
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const floatIcon = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  70% { box-shadow: 0 0 0 20px rgba(99, 102, 241, 0); }
  100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
`;

const glowBorder = keyframes`
  0% { border-color: rgba(99, 102, 241, 0.2); }
  50% { border-color: rgba(6, 182, 212, 0.6); }
  100% { border-color: rgba(99, 102, 241, 0.2); }
`;

// Planes de respaldo por si la base de datos está vacía
const fallbackPlans = [
  {
    speed: 50,
    price: 50,
    type: 'Inalámbrico Simétrico',
    tech: 'Antena Inalámbrica de Alta Ganancia',
    color: '#06b6d4',
    recommended: 'Navegación general y Redes Sociales',
    devices: 'Hasta 4 dispositivos',
    features: [
      'Velocidad de descarga estable de 50 Mbps',
      'Velocidad de subida simétrica de 50 Mbps',
      'Ideal para TikTok, Netflix HD y Zoom',
      'Instalación express de antena',
      'Sin contratos de permanencia forzada'
    ]
  },
  {
    speed: 100,
    price: 70,
    type: 'Fibra / Inalámbrico Híbrido',
    tech: 'Antena Ubiquiti / Fibra Híbrida',
    color: '#3b82f6',
    recommended: 'Teletrabajo y Video de Alta Definición',
    devices: 'Hasta 8 dispositivos',
    features: [
      'Velocidad fluida de 100 Mbps',
      'Baja latencia óptima para videollamadas',
      'Ideal para streaming Full HD y Home Office',
      'Soporte técnico preferente en portal',
      'Monitoreo dinámico del router en tiempo real'
    ]
  },
  {
    speed: 200,
    price: 90,
    type: 'Fibra Óptica Simétrica',
    tech: 'FTTH (Fibra hasta el hogar)',
    color: '#6366f1',
    recommended: 'Gaming competitivo y Streaming 4K Ultra',
    devices: 'Hasta 15 dispositivos',
    features: [
      'Enlace 100% Fibra Óptica de 200 Mbps',
      'Velocidad simétrica garantizada (subida/bajada)',
      'Ping ultra-bajo para juegos en línea (gaming)',
      'Router Dual Band de última generación',
      'Acceso completo a promociones por pago puntual'
    ]
  },
  {
    speed: 500,
    price: 130,
    type: 'Fibra Óptica Simétrica Pro',
    tech: 'FTTH Fibra Premium',
    color: '#8b5cf6',
    recommended: 'Hogares inteligentes y descargas masivas',
    devices: 'Más de 25 dispositivos',
    features: [
      'Conexión masiva de 500 Mbps simétricos',
      'Ideal para creadores de contenido y streaming 4K/8K',
      'Prioridad de tráfico sobre nodos MikroTik',
      'Monitoreo proactivo ante desconexiones',
      'Soporte técnico VIP dedicado 24/7'
    ]
  },
  {
    speed: 1000,
    price: 190,
    type: 'Fibra Giga-Velocidad Ultra',
    tech: 'FTTH Extremo',
    color: '#ec4899',
    recommended: 'Casas grandes, creadores y uso comercial',
    devices: 'Dispositivos ilimitados',
    features: [
      'Máxima velocidad del mercado: 1000 Mbps (1 Gbps)',
      'Simetría extrema para transferencias gigantes',
      'SLA de soporte técnico inmediato en < 12 horas',
      'Router Wi-Fi Mesh de alta potencia incluido',
      'Facturación simplificada con alertas vía WhatsApp'
    ]
  }
];

// FAQs de respaldo
const fallbackFaqs = [
  {
    question: '¿Qué significa velocidad simétrica en fibra óptica?',
    answer: 'Significa que tendrás exactamente la misma velocidad para descargar información (ver videos, navegar) que para subirla (enviar archivos, hacer videollamadas, jugar en línea). La mayoría de los operadores tradicionales ofrecen menos velocidad de subida, pero en YinoWISP garantizamos simetría total en nuestros planes de fibra.'
  },
  {
    question: '¿Cómo funciona el Portal del Cliente?',
    answer: 'Es tu centro digital de control. Al contratar nuestro servicio, recibes un usuario para acceder desde tu móvil o computadora. En el portal podrás ver tus facturas, reportar averías al equipo técnico, comprobar tus gráficos de consumo de internet en vivo e incluso pagar y subir tus comprobantes en segundos para evitar cortes de servicio.'
  },
  {
    question: '¿Tienen contratos de permanencia obligatoria?',
    answer: 'No. Creemos en la calidad de nuestro servicio. Si deseas suspender o cambiar tu plan, puedes hacerlo desde tu portal sin penalizaciones ni contratos forzosos. Queremos que te quedes con nosotros por gusto, no por obligación.'
  },
  {
    question: '¿Cuánto tiempo tarda la instalación del servicio?',
    answer: 'El tiempo promedio de instalación oscila entre 24 y 48 horas hábiles después de validar la cobertura. Nuestros técnicos de campo realizarán las pruebas de señal y te dejarán navegando el mismo día de la visita.'
  }
];

// Servicios de respaldo
const fallbackServices = [
  {
    id: 'fb-serv-1',
    name: 'Fibra Óptica Simétrica (FTTH)',
    category: 'Hogares y Negocios',
    description: 'La tecnología más moderna del mundo para una navegación ultra estable sin cortes. Misma velocidad de descarga y carga para compartir archivos y streaming sin límites.',
    image_url: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=80'
  },
  {
    id: 'fb-serv-2',
    name: 'Internet Inalámbrico Dedicado',
    category: 'Zonas Rurales y Periféricas',
    description: 'Llegamos donde otros no pueden con antenas CPE de última generación conectadas a nuestros nodos principales de fibra. Estabilidad garantizada con ancho de banda optimizado.',
    image_url: 'https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&w=600&q=80'
  },
  {
    id: 'fb-serv-3',
    name: 'Enlaces de Respaldo Corporativo',
    category: 'Empresas',
    description: 'Garantiza la continuidad de tu negocio con un enlace secundario redundante. Configuración de balanceo de carga y failover automático MikroTik para cero pérdidas de tiempo.',
    image_url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80'
  }
];

// Testimonios de respaldo
const fallbackTestimonials = [
  {
    id: 'fb-test-1',
    client_name: 'Carlos Mendoza',
    rating: 5,
    comment: 'Llevo 6 meses con la fibra óptica de YinoWISP y el servicio es excelente. Trabajo haciendo Home Office y las videollamadas no se cortan para nada. Muy recomendado.',
    district: 'San Juan de Lurigancho',
    avatar_url: ''
  },
  {
    id: 'fb-test-2',
    client_name: 'Ana Sofía Rodríguez',
    rating: 5,
    comment: 'Lo que más me gusta es el portal de clientes. Puedo reportar mi pago por Yape en segundos y descargar mis facturas. La velocidad contratada es real y simétrica.',
    district: 'Villa El Salvador',
    avatar_url: ''
  },
  {
    id: 'fb-test-3',
    client_name: 'Juan Carlos Huamán',
    rating: 5,
    comment: 'Instalaron el servicio al día siguiente de solicitar la cobertura. Las antenas inalámbricas son muy estables y la latencia para juegos como Dota 2 es bajísima.',
    district: 'Ate Vitarte',
    avatar_url: ''
  }
];

// Estadísticas de respaldo
const fallbackStatistics = [
  { id: 'fb-stat-1', val_count: 1500, label: 'Clientes Conectados' },
  { id: 'fb-stat-2', val_count: 8, label: 'Nodos de Red Activos' },
  { id: 'fb-stat-3', val_count: 99, label: 'Disponibilidad de Red (%)' },
  { id: 'fb-stat-4', val_count: 24, label: 'Soporte Técnico Horas (VIP)' }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();

  // ==========================================
  // ESTADOS PRINCIPALES
  // ==========================================
  const [landingInfo, setLandingInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selector interactivo de planes
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(2);

  // Modal de Cobertura y Términos Legales
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '', phone: '', email: '', address: '', district: '', interest_plan: '', observations: ''
  });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadAlert, setLeadAlert] = useState({ show: false, message: '', severity: 'success' });

  // Popup Promocional
  const [activePopup, setActivePopup] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // Menú móvil y scroll-spy
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  // Indice del banner activo en el carrusel
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);

  // ==========================================
  // CARGA DE DATOS DESDE LA API PÚBLICA
  // ==========================================
  const loadLandingData = useCallback(async () => {
    try {
      const res = await fetch('/api/landing/public/info');
      if (res.ok) {
        const data = await res.json();
        setLandingInfo(data);

        // Actualizar SEO dinámico
        if (data.settings?.seo_metadata) {
          const seo = data.settings.seo_metadata;
          if (seo.title) document.title = seo.title;
          
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = 'description';
            document.head.appendChild(metaDesc);
          }
          if (seo.description) metaDesc.content = seo.description;

          let metaKeywords = document.querySelector('meta[name="keywords"]');
          if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.name = 'keywords';
            document.head.appendChild(metaKeywords);
          }
          if (seo.keywords) metaKeywords.content = seo.keywords;
        }

        // Favicon
        if (data.settings?.favicon_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = data.settings.favicon_url;
        }

        // Scripts Analíticos (GTM / Analytics / Pixel)
        if (data.settings?.analytic_scripts) {
          const scripts = data.settings.analytic_scripts;
          if (scripts.gtm && !window.gtmInitialized) {
            window.gtmInitialized = true;
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer',scripts.gtm);
          }
        }

        // Mostrar Popup si existe y no se ha mostrado en la sesión
        if (data.popups && data.popups.length > 0) {
          const popupShown = sessionStorage.getItem('promoPopupShown');
          if (!popupShown) {
            setActivePopup(data.popups[0]);
            setPopupOpen(true);
            sessionStorage.setItem('promoPopupShown', 'true');
          }
        }
      }
    } catch (e) {
      console.error('Error cargando información pública de la landing:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLandingData();
  }, [loadLandingData]);

  // Manejar cambio automático de banners en el carrusel
  const activeBanners = landingInfo?.banners?.filter(b => b.is_active) || [];

  useEffect(() => {
    if (activeBanners.length <= 1) {
      setActiveBannerIndex(0);
      return;
    }
    const timer = setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % activeBanners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [activeBanners.length]);

  // ==========================================
  // CONFIGURACIÓN DE COLORES
  // ==========================================
  const settings = landingInfo?.settings || {};
  const primaryColor = settings.custom_colors?.primary || '#6366f1';
  const secondaryColor = settings.custom_colors?.secondary || '#06b6d4';
  const backgroundColor = settings.custom_colors?.background || '#0b0f19';

  // Estilo premium para inputs en modo oscuro
  const darkInputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.15)',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.35)',
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
        boxShadow: `0 0 10px ${alpha(primaryColor, 0.15)}`,
      },
      '& .MuiSelect-select': {
        color: '#ffffff',
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9ca3af',
      '&.Mui-focused': {
        color: primaryColor,
      },
    },
    '& .MuiSelect-icon': {
      color: '#9ca3af',
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      '&::placeholder': {
        color: '#9ca3af',
        opacity: 0.8
      }
    }
  };

  // ==========================================
  // PROCESAMIENTO DE PLANES
  // ==========================================
  const rawPlans = landingInfo?.plans || [];
  const plans = rawPlans.length > 0 ? rawPlans.map(plan => {
    let features = [];
    try {
      features = typeof plan.caracteristicas_adicionales === 'string' 
        ? JSON.parse(plan.caracteristicas_adicionales) 
        : (plan.caracteristicas_adicionales || []);
    } catch(e) {
      features = [];
    }

    if (features.length === 0) {
      features = [
        `Velocidad de descarga estable de ${plan.download_speed} Mbps`,
        plan.mostrar_subida_bajada ? `Velocidad de subida simétrica de ${plan.upload_speed} Mbps` : 'Conexión simétrica',
        plan.mostrar_instalacion_gratis ? 'Instalación gratis de router/antena' : 'Instalación profesional express',
        'Sin contratos de permanencia obligatoria'
      ];
    }

    return {
      id: plan.id,
      speed: plan.download_speed,
      price: plan.price,
      type: plan.description || (plan.download_speed >= 200 ? 'Fibra Óptica Simétrica' : 'Inalámbrico Simétrico'),
      tech: plan.download_speed >= 200 ? 'FTTH (Fibra hasta el hogar)' : 'Antena Inalámbrica de Alta Ganancia',
      color: plan.color_resaltado || '#6366f1',
      recommended: plan.uso_recomendado || (plan.download_speed >= 500 ? 'Hogares inteligentes y descargas masivas' : 'Navegación general y teletrabajo'),
      devices: plan.dispositivos_sugeridos || (plan.download_speed >= 500 ? 'Dispositivos ilimitados' : `Hasta ${Math.floor(plan.download_speed / 10) || 5} dispositivos`),
      features,
      es_destacado: plan.es_destacado,
      etiqueta: plan.etiqueta_web
    };
  }) : fallbackPlans;

  const activePlan = plans[selectedPlanIndex] || plans[0] || fallbackPlans[0];

  // ==========================================
  // GESTIÓN DE LEADS (COBERTURA)
  // ==========================================
  const handleOpenCoverage = (planName = '') => {
    setLeadForm({
      name: '', phone: '', email: '', address: '', district: '', interest_plan: planName, observations: ''
    });
    setCoverageOpen(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) {
      setLeadAlert({ show: true, message: 'Por favor, rellena tu nombre y número de teléfono.', severity: 'error' });
      return;
    }

    setLeadLoading(true);
    try {
      const response = await fetch('/api/landing/public/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadForm)
      });
      if (response.ok) {
        setLeadAlert({ show: true, message: '¡Solicitud registrada con éxito! Un asesor se pondrá en contacto contigo a la brevedad.', severity: 'success' });
        setTimeout(() => {
          setCoverageOpen(false);
          setLeadAlert({ show: false, message: '', severity: 'success' });
        }, 3000);
      } else {
        setLeadAlert({ show: true, message: 'Hubo un error al registrar tu solicitud. Por favor, intenta de nuevo.', severity: 'error' });
      }
    } catch (err) {
      setLeadAlert({ show: true, message: 'Error de red. Revisa tu conexión a internet.', severity: 'error' });
    } finally {
      setLeadLoading(false);
    }
  };

  // ==========================================
  // REDIRECCIÓN WHATSAPP DE LA CAMPAÑA
  // ==========================================
  const handleCampaignCTA = () => {
    const campaign = landingInfo?.campaign;
    if (!campaign) return;

    const waNumber = settings.whatsapp || '51999999999';
    const cleanNumber = waNumber.replace(/\D/g, '');
    const planInfo = campaign.plan_name ? `${campaign.plan_name} (S/ ${campaign.plan_price})` : 'Planes de Internet';
    const textMessage = `¡Hola! Vengo de la landing page y me gustaría solicitar la promoción de la campaña: "${campaign.title}" con el plan: ${planInfo}.`;
    
    window.open(`https://wa.me/${cleanNumber}?text=${encodeURIComponent(textMessage)}`, '_blank');
  };

  // Secciones ordenadas y activas
  const activeSections = landingInfo?.sections?.filter(s => s.is_active) || [
    { section_key: 'hero', title: 'Banner Principal' },
    { section_key: 'planes', title: 'Nuestros Planes' },
    { section_key: 'campaña', title: 'Campaña del Mes' },
    { section_key: 'servicios', title: 'Nuestros Servicios' },
    { section_key: 'beneficios', title: 'Beneficios del Servicio' },
    { section_key: 'testimonios', title: 'Testimonios de Clientes' },
    { section_key: 'faq', title: 'Preguntas Frecuentes' },
    { section_key: 'contacto', title: 'Formulario de Contacto' }
  ];

  // Mapeo de claves de secciones a nombres cortos del menú
  const menuTitles = {
    planes: 'Planes',
    servicios: 'Servicios',
    beneficios: 'Beneficios',
    faq: 'FAQ',
    contacto: 'Contacto'
  };

  // Helper para verificar visibilidad en el menú (solo secciones esenciales)
  const isSectionVisible = (key) => {
    if (key === 'planes') return plans.length > 0;
    if (key === 'servicios' || key === 'beneficios' || key === 'faq' || key === 'contacto') return true;
    // Omitir hero, campaña, estadísticas y testimonios para evitar amontonamiento
    return false;
  };

  // Scroll-spy para resaltar sección activa en menú
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120; // Compensación del menú pegajoso
      
      for (const sec of activeSections) {
        const el = document.getElementById(`${sec.section_key}-section`);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(sec.section_key);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSections]);

  const handleAdminAccess = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      navigate('/login');
    } else {
      window.location.href = 'https://admin.yinowisp.com/login';
    }
  };

  const handleClientAccess = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      navigate('/client');
    } else {
      window.location.href = 'https://cliente.yinowisp.com/client/';
    }
  };

  const handleButtonClick = (url) => {
    if (!url) {
      handleOpenCoverage();
      return;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
    } else if (url.startsWith('#')) {
      document.getElementById(url.replace('#', ''))?.scrollIntoView({ behavior: 'smooth' });
    } else {
      if (url.startsWith('/')) {
        navigate(url);
      } else {
        window.location.href = url;
      }
    }
  };

  const renderRightGrid = (banner) => {
    if (banner?.image_url) {
      return (
        <Box 
          sx={{ 
            position: 'relative', 
            width: { xs: '280px', sm: '360px', md: '100%' }, 
            aspectRatio: '16/10',
            borderRadius: '24px', 
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.7) 100%)', 
            border: '1px solid rgba(255, 255, 255, 0.1)', 
            boxShadow: `0 20px 40px rgba(0,0,0,0.4), 0 0 30px ${alpha(primaryColor, 0.15)}`, 
            animation: `${float} 6s ease-in-out infinite`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box 
            component="img" 
            src={banner.image_url} 
            alt={banner.title} 
            sx={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              borderRadius: '24px'
            }} 
          />
          <Box 
            sx={{ 
              position: 'absolute', 
              inset: 0, 
              background: 'linear-gradient(to top, rgba(11, 15, 25, 0.4) 0%, rgba(11, 15, 25, 0) 100%)',
              pointerEvents: 'none'
            }} 
          />
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: { xs: '300px', sm: '380px' }, height: { xs: '300px', sm: '380px' }, borderRadius: '40px', background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.8) 100%)', border: '1px solid rgba(255, 255, 255, 0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 30px 60px rgba(0,0,0,0.5)', animation: `${float} 6s ease-in-out infinite` }}>
        <Box sx={{ position: 'absolute', width: '75%', height: '75%', borderRadius: '50%', border: `2px dashed ${alpha(secondaryColor, 0.2)}`, animation: `${pulse} 5s infinite` }} />
        <Box sx={{ position: 'absolute', width: '50%', height: '50%', borderRadius: '50%', border: `1px dashed ${alpha(primaryColor, 0.3)}` }} />
        
        <Stack alignItems="center" spacing={1.5} sx={{ zIndex: 2 }}>
          <WifiIcon sx={{ fontSize: 84, color: secondaryColor }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#ffffff', letterSpacing: '0.05em' }}>
            {settings.company_name?.toUpperCase() || 'YINOWISP'} NETWORK
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            MikroTik Enrutamiento Activo
          </Typography>
        </Stack>
        
        <Box sx={{ position: 'absolute', top: '15%', left: '15%', bgcolor: alpha(primaryColor, 0.1), p: 1.5, borderRadius: '16px', border: `1px solid ${alpha(primaryColor, 0.3)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpeedIcon sx={{ color: primaryColor, fontSize: 20 }} />
          <Typography variant="caption" fontWeight="bold">Simetría 1:1</Typography>
        </Box>
        
        <Box sx={{ position: 'absolute', bottom: '20%', right: '10%', bgcolor: alpha(secondaryColor, 0.1), p: 1.5, borderRadius: '16px', border: `1px solid ${alpha(secondaryColor, 0.3)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon sx={{ color: secondaryColor, fontSize: 20 }} />
          <Typography variant="caption" fontWeight="bold">SSL Seguro</Typography>
        </Box>
      </Box>
    );
  };

  // ==========================================
  // RENDER: MODO MANTENIMIENTO
  // ==========================================
  if (settings.maintenance_mode) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(135deg, #0b0f19 0%, #111827 50%, #030712 100%)', 
          color: '#f3f4f6',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: 'Outfit, Inter, sans-serif',
          p: 3,
          textAlign: 'center'
        }}
      >
        <Card sx={{ background: 'rgba(30, 41, 59, 0.35)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '24px', p: 5, maxWidth: '500px', backdropFilter: 'blur(12px)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
          <WifiIcon sx={{ fontSize: 72, color: primaryColor, mb: 3, animation: `${floatIcon} 3s ease-in-out infinite` }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
            Sitio en Mantenimiento
          </Typography>
          <Typography variant="body1" sx={{ color: '#9ca3af', mb: 4, lineHeight: 1.6 }}>
            {settings.maintenance_message || 'Estamos realizando mejoras programadas en nuestra página web. Volveremos muy pronto con novedades.'}
          </Typography>
          
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 3 }} />
          
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Button variant="contained" onClick={() => handleClientAccess()} sx={{ bgcolor: primaryColor, color: '#ffffff', py: 1.5, borderRadius: '12px', textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: alpha(primaryColor, 0.85) } }}>
              Acceder al Portal del Cliente
            </Button>
            <Button variant="outlined" onClick={() => handleAdminAccess()} sx={{ borderColor: 'rgba(255,255,255,0.1)', color: '#ffffff', py: 1.5, borderRadius: '12px', textTransform: 'none', '&:hover': { borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.02)' } }}>
              Acceso Administrativo
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  // ==========================================
  // CARGA CARGANDO PANTALLA
  // ==========================================
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#0b0f19', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={50} sx={{ color: primaryColor }} />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        background: `linear-gradient(135deg, ${backgroundColor} 0%, #111827 50%, #030712 100%)`, 
        color: '#f3f4f6',
        fontFamily: 'Outfit, Inter, sans-serif',
        overflowX: 'hidden',
        position: 'relative'
      }}
    >
      {/* Estilos Dinámicos Inyectados */}
      <style>{`
        :root {
          --primary-color: ${primaryColor};
          --secondary-color: ${secondaryColor};
          --bg-color: ${backgroundColor};
        }
      `}</style>

      {/* Luces de fondo estilo Nebula */}
      <Box sx={{ position: 'absolute', top: '5%', left: '10%', width: '350px', height: '350px', borderRadius: '50%', background: `alpha(${primaryColor}, 0.12)`, filter: 'blur(100px)', zIndex: 0, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', top: '40%', right: '5%', width: '450px', height: '450px', borderRadius: '50%', background: `alpha(${secondaryColor}, 0.08)`, filter: 'blur(130px)', zIndex: 0, pointerEvents: 'none' }} />

      {/* HEADER / NAVIGATION */}
      <Box 
        component="header" 
        sx={{ 
          py: { xs: 1.5, md: 2 }, 
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px) saturate(180%)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          backgroundColor: 'rgba(11, 15, 25, 0.85)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s ease'
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" justifyContent="space-between">
            {/* Logo y Nombre */}
            <Grid item>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                {settings.logo_url ? (
                  <Box component="img" src={settings.logo_url} sx={{ height: 36, width: 'auto', objectFit: 'contain' }} />
                ) : (
                  <Box sx={{ p: 1, borderRadius: '12px', background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, boxShadow: `0 4px 18px ${alpha(primaryColor, 0.3)}`, animation: `${floatIcon} 3s ease-in-out infinite` }}>
                    <WifiIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                  </Box>
                )}
                <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: '-0.03em', background: 'linear-gradient(to right, #ffffff 40%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'flex', alignItems: 'center', fontSize: { xs: '1.2rem', sm: '1.4rem' } }}>
                  {settings.company_name || 'YinoWISP'}
                </Typography>
              </Stack>
            </Grid>

            {/* Menú de Navegación de Escritorio */}
            <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
              <Stack direction="row" spacing={{ md: 1, lg: 2 }} alignItems="center">
                {activeSections.filter(sec => isSectionVisible(sec.section_key)).map((sec) => {
                  const isActive = activeSection === sec.section_key;
                  return (
                    <Box
                      key={sec.section_key}
                      onClick={() => document.getElementById(`${sec.section_key}-section`)?.scrollIntoView({ behavior: 'smooth' })}
                      sx={{ 
                        color: isActive ? '#ffffff' : '#9ca3af', 
                        fontWeight: 600, 
                        cursor: 'pointer', 
                        transition: 'all 0.2s ease',
                        fontSize: { md: '0.8rem', lg: '0.85rem' },
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        px: 2,
                        py: 1,
                        borderRadius: '12px',
                        position: 'relative',
                        background: isActive ? alpha(primaryColor, 0.08) : 'transparent',
                        border: isActive ? `1px solid ${alpha(primaryColor, 0.2)}` : '1px solid transparent',
                        '&:hover': { 
                          color: '#ffffff', 
                          background: isActive ? alpha(primaryColor, 0.12) : 'rgba(255, 255, 255, 0.04)',
                          borderColor: isActive ? alpha(primaryColor, 0.3) : 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      {menuTitles[sec.section_key] || sec.title}
                    </Box>
                  );
                })}
              </Stack>
            </Grid>
            
            {/* Botones de Acción */}
            <Grid item>
              <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                <Button 
                  variant="outlined" 
                  onClick={() => handleClientAccess()} 
                  sx={{ 
                    color: '#ffffff', 
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    textTransform: 'none', 
                    borderRadius: '20px',
                    px: 2.2, 
                    py: 0.8,
                    display: { xs: 'none', sm: 'inline-flex' }, 
                    fontSize: '0.8rem', 
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      color: '#ffffff',
                      background: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                    } 
                  }}
                >
                  Portal Cliente
                </Button>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={() => navigate('/signup')} 
                  sx={{ 
                    borderRadius: '20px', 
                    textTransform: 'none', 
                    fontWeight: 800, 
                    px: 2.5, 
                    py: 0.9,
                    fontSize: '0.85rem',
                    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)'
                  }}
                >
                  🚀 Crear Mi WISP
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => handleOpenCoverage()} 
                  sx={{ 
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, 
                    color: '#ffffff', 
                    borderRadius: '20px', 
                    textTransform: 'none', 
                    fontWeight: 700, 
                    px: 2.5, 
                    py: 0.9,
                    display: { xs: 'none', sm: 'inline-flex' },
                    fontSize: '0.8rem', 
                    boxShadow: `0 4px 15px ${alpha(primaryColor, 0.35)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, 
                      filter: 'brightness(1.1)',
                      boxShadow: `0 6px 20px ${alpha(primaryColor, 0.45)}`,
                      transform: 'translateY(-1px)'
                    } 
                  }}
                >
                  Consultar Cobertura
                </Button>

                {/* Icono de Hamburguesa para Móvil */}
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{ 
                    display: { xs: 'flex', md: 'none' }, 
                    color: '#ffffff', 
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    '&:hover': { background: 'rgba(255, 255, 255, 0.08)' } 
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </Container>

        {/* Menú Desplegable Lateral Móvil */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{
            sx: {
              width: '280px',
              background: 'linear-gradient(135deg, rgba(11, 15, 25, 0.96) 0%, rgba(17, 24, 39, 0.99) 100%)',
              backdropFilter: 'blur(20px)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              p: 3,
              color: '#f3f4f6'
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h6" fontWeight="bold">Navegación</Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#ffffff' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            {activeSections.filter(sec => isSectionVisible(sec.section_key)).map((sec) => {
              const isActive = activeSection === sec.section_key;
              return (
                <Typography
                  key={sec.section_key}
                  variant="h6"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    // Retardo mínimo para cerrar drawer antes de scroll
                    setTimeout(() => {
                      document.getElementById(`${sec.section_key}-section`)?.scrollIntoView({ behavior: 'smooth' });
                    }, 250);
                  }}
                  sx={{ 
                    color: isActive ? primaryColor : '#9ca3af', 
                    fontWeight: 600, 
                    cursor: 'pointer', 
                    fontSize: '1.05rem',
                    transition: 'all 0.2s ease',
                    '&:hover': { color: primaryColor, pl: 1 }
                  }}
                >
                  {menuTitles[sec.section_key] || sec.title}
                </Typography>
              );
            })}
            
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2 }} />

            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => {
                setMobileMenuOpen(false);
                handleClientAccess();
              }} 
              sx={{ 
                borderColor: 'rgba(255,255,255,0.12)', 
                color: '#ffffff', 
                borderRadius: '12px', 
                py: 1.5, 
                textTransform: 'none', 
                fontWeight: 600,
                '&:hover': { borderColor: primaryColor, background: alpha(primaryColor, 0.05) } 
              }}
            >
              Portal Cliente
            </Button>

            <Button 
              variant="contained" 
              fullWidth 
              onClick={() => {
                setMobileMenuOpen(false);
                handleOpenCoverage();
              }} 
              sx={{ 
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, 
                color: '#ffffff', 
                borderRadius: '12px', 
                py: 1.5, 
                textTransform: 'none', 
                fontWeight: 700, 
                '&:hover': { filter: 'brightness(1.1)' } 
              }}
            >
              Consultar Cobertura
            </Button>

            <Typography
              variant="caption"
              align="center"
              onClick={() => {
                setMobileMenuOpen(false);
                handleAdminAccess();
              }}
              sx={{
                color: '#4b5563',
                cursor: 'pointer',
                mt: 2,
                '&:hover': { color: '#9ca3af' }
              }}
            >
              Acceso de Administradores
            </Typography>
          </Stack>
        </Drawer>
      </Box>

      {/* RENDERIZADO DINÁMICO DE SECCIONES EN ORDEN */}
      {activeSections.map((sec) => {
        // ==========================================
        // SECCIÓN: HERO / BANNER PRINCIPAL
        // ==========================================
        if (sec.section_key === 'hero') {
          if (activeBanners.length > 0) {
            const currentBanner = activeBanners[activeBannerIndex] || activeBanners[0];
            return (
              <Container key="hero" id="hero-section" maxWidth="lg" sx={{ pt: { xs: 12, md: 18 }, pb: { xs: 8, md: 12 }, zIndex: 1, position: 'relative', scrollMarginTop: '88px' }}>
                <Fade key={activeBannerIndex} in={true} timeout={600}>
                  <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 2.2, borderRadius: '30px', bgcolor: alpha(primaryColor, 0.08), border: `1px solid ${alpha(primaryColor, 0.18)}`, color: primaryColor, fontWeight: 600, fontSize: '0.85rem', mb: 4.5 }}>
                        <FiberIcon sx={{ fontSize: 18 }} /> 
                        Novedades & Promociones
                      </Box>
                      
                      <Typography variant="h1" fontWeight={900} gutterBottom sx={{ fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4.4rem' }, lineHeight: 1.1, letterSpacing: '-0.04em', mb: 3, color: '#ffffff' }}>
                        {currentBanner.title}
                      </Typography>
                      
                      <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '620px', mb: 6, fontSize: { xs: '1.05rem', sm: '1.25rem' }, fontWeight: 400, lineHeight: 1.6 }}>
                        {currentBanner.description}
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                        <Button 
                          variant="contained" 
                          onClick={() => handleButtonClick(currentBanner.button_url)} 
                          sx={{ 
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, 
                            color: '#ffffff', 
                            fontWeight: 700, 
                            borderRadius: '16px', 
                            textTransform: 'none', 
                            px: 4.5, 
                            py: 2, 
                            fontSize: '1rem', 
                            boxShadow: `0 4px 25px ${alpha(primaryColor, 0.4)}`, 
                            '&:hover': { 
                              transform: 'translateY(-2px)', 
                              boxShadow: `0 6px 30px ${alpha(primaryColor, 0.55)}`, 
                              filter: 'brightness(1.08)' 
                            } 
                          }}
                        >
                          {currentBanner.button_text || 'Más Información'}
                        </Button>
                        <Button variant="outlined" onClick={() => handleOpenCoverage()} sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', color: '#ffffff', fontWeight: 600, borderRadius: '16px', textTransform: 'none', px: 4, py: 2, fontSize: '1rem', background: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(8px)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)', background: 'rgba(255, 255, 255, 0.06)' } }}>
                          Consultar Cobertura
                        </Button>
                      </Stack>
                    </Grid>
                    
                    <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                      {renderRightGrid(currentBanner)}
                    </Grid>
                  </Grid>
                </Fade>
                {activeBanners.length > 1 && (
                  <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 5 }}>
                    {activeBanners.map((_, idx) => (
                      <Box
                        key={idx}
                        onClick={() => setActiveBannerIndex(idx)}
                        sx={{
                          width: activeBannerIndex === idx ? 28 : 10,
                          height: 10,
                          borderRadius: 5,
                          bgcolor: activeBannerIndex === idx ? primaryColor : 'rgba(255, 255, 255, 0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: activeBannerIndex === idx ? `0 0 10px ${primaryColor}` : 'none',
                          '&:hover': {
                            bgcolor: activeBannerIndex === idx ? primaryColor : 'rgba(255, 255, 255, 0.4)'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Container>
            );
          }

          return (
            <Container key="hero" id="hero-section" maxWidth="lg" sx={{ pt: { xs: 12, md: 18 }, pb: { xs: 8, md: 12 }, zIndex: 1, position: 'relative', scrollMarginTop: '88px' }}>
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 2.2, borderRadius: '30px', bgcolor: alpha(primaryColor, 0.08), border: `1px solid ${alpha(primaryColor, 0.18)}`, color: primaryColor, fontWeight: 600, fontSize: '0.85rem', mb: 4.5 }}>
                    <FiberIcon sx={{ fontSize: 18 }} /> 
                    Fibra Óptica & Enlaces Simétricos Dedicados
                  </Box>
                  
                  <Typography variant="h1" fontWeight={900} gutterBottom sx={{ fontSize: { xs: '2.8rem', sm: '3.8rem', md: '4.8rem' }, lineHeight: 1.05, letterSpacing: '-0.04em', mb: 3, color: '#ffffff' }}>
                    {settings.slogan ? settings.slogan.split(' con ')[0] : 'Internet de alta velocidad'}
                    <Box component="span" sx={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor}, #60a5fa)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'block' }}>
                      {settings.slogan ? (settings.slogan.includes(' con ') ? `con ${settings.slogan.split(' con ')[1]}` : '') : 'con control total digital'}
                    </Box>
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '620px', mb: 6, fontSize: { xs: '1.05rem', sm: '1.25rem' }, fontWeight: 400, lineHeight: 1.6 }}>
                    {settings.description || 'Conéctate a la red más estable de fibra óptica simétrica y antenas de alta fidelidad. Gestiona facturas, mide tu tráfico en vivo y solicita soporte técnico desde tu propio portal digital.'}
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <Button variant="contained" onClick={() => document.getElementById('planes-section')?.scrollIntoView({ behavior: 'smooth' })} sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#ffffff', fontWeight: 700, borderRadius: '16px', textTransform: 'none', px: 4.5, py: 2, fontSize: '1rem', boxShadow: `0 4px 25px ${alpha(primaryColor, 0.4)}`, '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 30px ${alpha(primaryColor, 0.55)}`, filter: 'brightness(1.08)' } }}>
                      Ver Planes de Internet
                    </Button>
                    <Button variant="outlined" onClick={() => handleOpenCoverage()} sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', color: '#ffffff', fontWeight: 600, borderRadius: '16px', textTransform: 'none', px: 4, py: 2, fontSize: '1rem', background: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(8px)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)', background: 'rgba(255, 255, 255, 0.06)' } }}>
                      Consultar Cobertura
                    </Button>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                  {renderRightGrid(null)}
                </Grid>
              </Grid>
            </Container>
          );
        }

        // ==========================================
        // SECCIÓN: PLANES DE INTERNET
        // ==========================================
        if (sec.section_key === 'planes') {
          return (
            <Box key="planes" id="planes-section" sx={{ py: 12, bgcolor: 'rgba(17, 24, 39, 0.3)', borderTop: '1px solid rgba(255, 255, 255, 0.04)', borderBottom: '1px solid rgba(255, 255, 255, 0.04)', position: 'relative', scrollMarginTop: '88px' }}>
              <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: primaryColor, textTransform: 'uppercase', tracking: '0.1em', mb: 1.5 }}>
                    Calcula y Elige tu Velocidad
                  </Typography>
                  <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.02em', mb: 3 }}>
                    Planes que se adaptan a tu ritmo
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '600px', mx: 'auto' }}>
                    Navega a la máxima velocidad y elige el plan ideal para tu hogar o negocio.
                  </Typography>
                </Box>

                <Card sx={{ background: 'rgba(30, 41, 59, 0.3)', backdropFilter: 'blur(16px)', borderRadius: '32px', border: '1px solid rgba(255, 255, 255, 0.06)', p: { xs: 3, md: 6 }, boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: `${glowBorder} 6s infinite ease-in-out` }}>
                  {/* Visualización de la Velocidad seleccionada */}
                  <Stack alignItems="center" spacing={1} sx={{ mb: 6 }}>
                    <Typography variant="h2" fontWeight={900} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, color: activePlan.color }}>
                      {activePlan.speed}
                      <Typography component="span" variant="h4" fontWeight="bold" color="textSecondary">
                        Mbps
                      </Typography>
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#ffffff' }}>
                      {activePlan.type}
                    </Typography>
                    {activePlan.etiqueta && (
                      <Chip label={activePlan.etiqueta} size="small" sx={{ bgcolor: alpha(activePlan.color, 0.15), color: activePlan.color, borderColor: alpha(activePlan.color, 0.3), border: '1px solid', fontWeight: 600, mb: 1 }} />
                    )}
                    <Chip label={activePlan.tech} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#9ca3af', fontWeight: 600 }} />
                  </Stack>

                  {/* Slider de MUI interactivo */}
                  <Box sx={{ px: { xs: 1, sm: 4 }, mb: 6 }}>
                    <Slider
                      value={selectedPlanIndex}
                      min={0}
                      max={plans.length - 1}
                      step={1}
                      marks={plans.map((p, idx) => ({ value: idx, label: `${p.speed} Mbps` }))}
                      onChange={(e, val) => setSelectedPlanIndex(val)}
                      sx={{
                        color: activePlan.color,
                        height: 10,
                        '& .MuiSlider-thumb': {
                          width: 26,
                          height: 26,
                          backgroundColor: '#ffffff',
                          border: `4px solid ${activePlan.color}`,
                          boxShadow: `0 0 15px ${activePlan.color}`,
                          '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: `0 0 25px ${activePlan.color}` }
                        },
                        '& .MuiSlider-track': { height: 10, borderRadius: 5 },
                        '& .MuiSlider-rail': { height: 10, borderRadius: 5, backgroundColor: '#374151' },
                        '& .MuiSlider-markLabel': { color: '#9ca3af', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, mt: 1.5 }
                      }}
                    />
                  </Box>

                  {/* Detalles del Plan y el Precio */}
                  <Grid container spacing={4} alignItems="center" sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ color: '#9ca3af', fontWeight: 'bold', mb: 2, textTransform: 'uppercase' }}>
                        ¿Qué incluye este plan?
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {activePlan.features.map((feature, idx) => (
                          <ListItem key={idx} sx={{ p: 0, mb: 1.5, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 32, mt: 0.2 }}>
                              <CheckIcon sx={{ color: activePlan.color, fontSize: 18 }} />
                            </ListItemIcon>
                            <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2', sx: { color: '#e5e7eb', lineHeight: 1.4 } }} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ textAlign: 'center', borderLeft: { md: '1px solid rgba(255,255,255,0.06)' } }}>
                      <Typography variant="body2" sx={{ color: '#9ca3af', mb: 1 }}>
                        Uso recomendado: <strong>{activePlan.recommended}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af', mb: 4 }}>
                        Dispositivos sugeridos: <strong>{activePlan.devices}</strong>
                      </Typography>

                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h3" fontWeight={900} sx={{ display: 'inline-flex', alignItems: 'baseline', color: '#ffffff' }}>
                          S/ {activePlan.price}
                          <Typography component="span" variant="subtitle1" color="textSecondary" sx={{ ml: 1 }}>
                            / mes
                          </Typography>
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleOpenCoverage(`${activePlan.speed} Mbps - S/ ${activePlan.price}`)}
                        sx={{
                          bgcolor: activePlan.color,
                          color: '#ffffff',
                          fontWeight: 'bold',
                          py: 2,
                          borderRadius: '16px',
                          fontSize: '1rem',
                          textTransform: 'none',
                          boxShadow: `0 4px 20px ${alpha(activePlan.color, 0.3)}`,
                          '&:hover': {
                            bgcolor: alpha(activePlan.color, 0.85),
                            boxShadow: `0 6px 25px ${alpha(activePlan.color, 0.45)}`
                          }
                        }}
                      >
                        Consultar Cobertura del Plan
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: CAMPAÑA DEL MES
        // ==========================================
        if (sec.section_key === 'campaña') {
          const campaign = landingInfo?.campaign;
          if (!campaign) return null;
          const campColor = campaign.campaign_color || '#ef4444';

          return (
            <Box key="campaña" id="campaña-section" sx={{ py: 12, position: 'relative', scrollMarginTop: '88px' }}>
              <Container maxWidth="lg">
                <Card sx={{ background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.9) 100%)', border: `1px solid ${alpha(campColor, 0.2)}`, borderRadius: '32px', overflow: 'hidden', boxShadow: `0 15px 40px ${alpha(campColor, 0.1)}` }}>
                  <Grid container>
                    <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 8 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, py: 0.5, px: 1.5, borderRadius: '20px', bgcolor: alpha(campColor, 0.1), border: `1px solid ${alpha(campColor, 0.3)}`, color: campColor, fontWeight: 'bold', fontSize: '0.8rem', mb: 3, width: 'fit-content' }}>
                        ¡PROMOCIÓN DE LA SEMANA!
                      </Box>
                      <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#ffffff', letterSpacing: '-0.02em' }}>
                        {campaign.title}
                      </Typography>
                      {campaign.subtitle && (
                        <Typography variant="h6" fontWeight="medium" sx={{ color: campColor, mb: 3 }}>
                          {campaign.subtitle}
                        </Typography>
                      )}
                      <Typography variant="body1" sx={{ color: '#9ca3af', mb: 5, lineHeight: 1.6 }}>
                        {campaign.description}
                      </Typography>

                      {campaign.plan_name && (
                        <Card sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', p: 3, mb: 5, maxWidth: '400px' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle2" color="textSecondary">Plan Promocionado</Typography>
                              <Typography variant="h6" fontWeight="bold">{campaign.plan_name}</Typography>
                              <Typography variant="body2" color="textSecondary">{campaign.plan_download} Mbps Simétricos</Typography>
                            </Box>
                            <Typography variant="h4" fontWeight="black" sx={{ color: campColor }}>
                              S/ {campaign.plan_price}
                            </Typography>
                          </Stack>
                        </Card>
                      )}

                      <Button
                        variant="contained"
                        onClick={handleCampaignCTA}
                        sx={{
                          bgcolor: campColor,
                          color: '#ffffff',
                          fontWeight: 'bold',
                          borderRadius: '16px',
                          px: 5,
                          py: 2,
                          fontSize: '1rem',
                          textTransform: 'none',
                          boxShadow: `0 4px 25px ${alpha(campColor, 0.35)}`,
                          width: 'fit-content',
                          '&:hover': {
                            bgcolor: alpha(campColor, 0.85),
                            boxShadow: `0 6px 30px ${alpha(campColor, 0.5)}`
                          }
                        }}
                      >
                        {campaign.button_text || '¡Solicitar Promoción por WhatsApp!'}
                      </Button>
                    </Grid>
                    
                    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', minHeight: 400 }}>
                      {campaign.image_url ? (
                        <Box component="img" src={campaign.image_url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${alpha(campColor, 0.2)}, ${alpha(backgroundColor, 0.9)})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FiberIcon sx={{ fontSize: 120, color: alpha(campColor, 0.15) }} />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Card>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: NUESTROS SERVICIOS
        // ==========================================
        if (sec.section_key === 'servicios') {
          const services = (landingInfo?.services && landingInfo.services.length > 0)
            ? landingInfo.services
            : fallbackServices;

          return (
            <Box key="servicios" id="servicios-section" sx={{ py: 12, bgcolor: 'rgba(17, 24, 39, 0.2)', borderTop: '1px solid rgba(255, 255, 255, 0.04)', borderBottom: '1px solid rgba(255, 255, 255, 0.04)', scrollMarginTop: '88px' }}>
              <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: secondaryColor, textTransform: 'uppercase', tracking: '0.12em', mb: 1.5 }}>
                    Soluciones de Conectividad
                  </Typography>
                  <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.025em', mb: 2 }}>
                    Nuestros Servicios Destacados
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '600px', mx: 'auto' }}>
                    Ofrecemos la tecnología adecuada para garantizar estabilidad a hogares y empresas.
                  </Typography>
                </Box>

                <Grid container spacing={4}>
                  {services.map((serv, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={serv.id}>
                      <Card sx={{ height: '100%', background: 'rgba(30, 41, 59, 0.25)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '24px', overflow: 'hidden', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', borderColor: alpha(secondaryColor, 0.4), background: 'rgba(30, 41, 59, 0.4)' } }}>
                        {serv.image_url && (
                          <Box component="img" src={serv.image_url} sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                        )}
                        <CardContent sx={{ p: 4 }}>
                          {serv.category && (
                            <Chip label={serv.category} size="small" sx={{ bgcolor: alpha(secondaryColor, 0.1), color: secondaryColor, fontWeight: 'bold', mb: 2 }} />
                          )}
                          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
                            {serv.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9ca3af', lineHeight: 1.6 }}>
                            {serv.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: BENEFICIOS
        // ==========================================
        if (sec.section_key === 'beneficios') {
          const apiBenefits = landingInfo?.benefits || [];
          
          const benefits = apiBenefits.length > 0 ? apiBenefits.map(b => {
            let icon = <CheckIcon sx={{ fontSize: 32 }} />;
            if (b.icon_name?.toLowerCase().includes('speed') || b.icon_name?.toLowerCase().includes('velocidad')) icon = <SpeedIcon sx={{ fontSize: 32 }} />;
            else if (b.icon_name?.toLowerCase().includes('fiber') || b.icon_name?.toLowerCase().includes('fibra')) icon = <FiberIcon sx={{ fontSize: 32 }} />;
            else if (b.icon_name?.toLowerCase().includes('support') || b.icon_name?.toLowerCase().includes('soporte')) icon = <SupportIcon sx={{ fontSize: 32 }} />;
            else if (b.icon_name?.toLowerCase().includes('security') || b.icon_name?.toLowerCase().includes('seguridad')) icon = <SecurityIcon sx={{ fontSize: 32 }} />;

            return {
              id: b.id,
              title: b.title,
              desc: b.description,
              icon,
              color: b.icon_name?.includes('speed') ? primaryColor : (b.icon_name?.includes('fiber') ? secondaryColor : '#3b82f6')
            };
          }) : [
            {
              icon: <SpeedIcon sx={{ fontSize: 32 }} />,
              title: 'Baja Latencia / Ping Estable',
              desc: 'Optimizamos nuestras rutas constantemente para asegurar el ping más bajo posible, perfecto para streaming, gaming online y videollamadas fluidas.',
              color: primaryColor
            },
            {
              icon: <FiberIcon sx={{ fontSize: 32 }} />,
              title: 'Banda Ancha Simétrica',
              desc: 'Olvídate de las subidas lentas. Nuestros enlaces de fibra óptica y antenas inalámbricas garantizan la misma velocidad de carga y descarga.',
              color: secondaryColor
            },
            {
              icon: <SupportIcon sx={{ fontSize: 32 }} />,
              title: 'Soporte y Respuestas Ágiles',
              desc: 'Atención automatizada por WhatsApp y tickets directos al centro de operaciones. Resolvemos tus inquietudes de inmediato sin intermediarios.',
              color: '#3b82f6'
            },
            {
              icon: <SecurityIcon sx={{ fontSize: 32 }} />,
              title: 'Seguridad y Conexión Cifrada',
              desc: 'Toda tu navegación e inicio de sesión están resguardados bajo estándares estrictos y protocolos SSL. Tu privacidad es nuestra prioridad.',
              color: '#8b5cf6'
            }
          ];

          return (
            <Box key="beneficios" id="beneficios-section" sx={{ py: 12, borderBottom: '1px solid rgba(255, 255, 255, 0.04)', scrollMarginTop: '88px' }}>
              <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: primaryColor, textTransform: 'uppercase', tracking: '0.12em', mb: 1.5 }}>
                    Ventajas Competitivas
                  </Typography>
                  <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.025em', mb: 2 }}>
                    ¿Por qué elegir YinoWISP?
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '600px', mx: 'auto' }}>
                    Descubre los pilares que hacen de nuestra red la opción favorita para hogares y empresas.
                  </Typography>
                </Box>

                <Grid container spacing={4} sx={{ mb: 10 }}>
                  {benefits.map((b, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={b.id || idx}>
                      <Card sx={{ height: '100%', background: 'rgba(30, 41, 59, 0.25)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '20px', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-6px)', borderColor: alpha(b.color || primaryColor, 0.4), background: 'rgba(30, 41, 59, 0.4)', boxShadow: `0 10px 25px ${alpha(b.color || primaryColor, 0.08)}` } }}>
                        <CardContent sx={{ p: 4 }}>
                          <Box sx={{ width: '56px', height: '56px', borderRadius: '14px', bgcolor: alpha(b.color || primaryColor, 0.1), color: b.color || primaryColor, display: 'flex', alignItems: 'center', justify: 'center', mb: 3 }}>
                            {b.icon}
                          </Box>
                          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
                            {b.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9ca3af', lineHeight: 1.6 }}>
                            {b.desc}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* HIGHLIGHT: Portal del Cliente Mockup */}
                <Grid container spacing={8} alignItems="center" sx={{ mt: 4 }}>
                  <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                    <Box sx={{ position: 'relative', p: 3, borderRadius: '32px', background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.8) 0%, rgba(3, 7, 18, 0.9) 100%)', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 25px 50px rgba(0,0,0,0.4)' }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }} />
                        <Typography variant="caption" sx={{ color: '#4b5563', pl: 2, fontMono: true }}>portal.{settings.company_name?.toLowerCase() || 'yinowisp'}.com</Typography>
                      </Stack>

                      <Stack spacing={2.5}>
                        <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                              <WalletIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">Pagos y Facturas</Typography>
                              <Typography variant="caption" color="textSecondary">Visualiza y paga en segundos</Typography>
                            </Box>
                          </Stack>
                          <Chip label="Al Día" size="small" color="success" sx={{ fontWeight: 'bold' }} />
                        </Box>

                        <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: alpha(primaryColor, 0.1), color: primaryColor }}>
                              <ChartIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">Consumo de Tráfico</Typography>
                              <Typography variant="caption" color="textSecondary">Gráfico en tiempo real (MikroTik)</Typography>
                            </Box>
                          </Stack>
                          <Box sx={{ width: '60px', height: '20px', borderRadius: '4px', border: `1px dashed ${primaryColor}` }} />
                        </Box>

                        <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: alpha(secondaryColor, 0.1), color: secondaryColor }}>
                              <SupportIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">Reportes e Incidencias</Typography>
                              <Typography variant="caption" color="textSecondary">Tickets de soporte directo</Typography>
                            </Box>
                          </Stack>
                          <Chip label="Resuelto" size="small" variant="outlined" color="info" />
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: secondaryColor, textTransform: 'uppercase', tracking: '0.12em', mb: 2 }}>
                      Tu cuenta de Cliente
                    </Typography>
                    <Typography variant="h3" fontWeight={900} gutterBottom sx={{ letterSpacing: '-0.025em', mb: 3 }}>
                      Autogestión total con tu Portal de Cliente
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#9ca3af', mb: 5, lineHeight: 1.7 }}>
                      Olvídate de las llamadas de espera o trámites lentos. Con tu cuenta de cliente de YinoWISP tienes el control administrativo e interactivo de tu conexión de internet en tus manos.
                    </Typography>

                    <Grid container spacing={3.5} sx={{ mb: 6 }}>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <CheckIcon sx={{ color: secondaryColor, mt: 0.5 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff' }}>Descarga Facturas</Typography>
                            <Typography variant="body2" color="textSecondary">Verifica tus consumos mensuales y descarga tus recibos en PDF cuando quieras.</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <CheckIcon sx={{ color: secondaryColor, mt: 0.5 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff' }}>Gráficos en Vivo</Typography>
                            <Typography variant="body2" color="textSecondary">Monitorea tus velocidades de subida y descarga directo desde las antenas o fibra.</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <CheckIcon sx={{ color: secondaryColor, mt: 0.5 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff' }}>Tickets de Averías</Typography>
                            <Typography variant="body2" color="textSecondary">Crea reportes que son asignados en el acto a técnicos de campo con seguimiento GPS.</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <CheckIcon sx={{ color: secondaryColor, mt: 0.5 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff' }}>Descuentos y Promos</Typography>
                            <Typography variant="body2" color="textSecondary">Ahorra en tu factura mensual activando bonificaciones y promociones especiales.</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Button variant="contained" onClick={() => handleClientAccess()} sx={{ bgcolor: secondaryColor, color: '#0b0f19', fontWeight: 800, borderRadius: '14px', px: 4, py: 1.8, textTransform: 'none', boxShadow: `0 4px 18px ${alpha(secondaryColor, 0.3)}`, '&:hover': { bgcolor: alpha(secondaryColor, 0.85), boxShadow: `0 6px 22px ${alpha(secondaryColor, 0.45)}` } }}>
                      Ingresar al Portal del Cliente
                    </Button>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: ESTADÍSTICAS
        // ==========================================
        if (sec.section_key === 'estadísticas') {
          const statistics = (landingInfo?.statistics && landingInfo.statistics.length > 0)
            ? landingInfo.statistics
            : fallbackStatistics;

          return (
            <Box key="estadísticas" id="estadísticas-section" sx={{ py: 8, background: `linear-gradient(to right, ${alpha(primaryColor, 0.05)}, ${alpha(secondaryColor, 0.05)})`, borderTop: '1px solid rgba(255, 255, 255, 0.04)', borderBottom: '1px solid rgba(255, 255, 255, 0.04)', scrollMarginTop: '88px' }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} justifyContent="center">
                  {statistics.map((stat) => (
                    <Grid item xs={6} md={3} key={stat.id} sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" fontWeight="900" sx={{ color: primaryColor, mb: 1 }}>
                        {stat.val_count}+
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af', fontWeight: 'bold', textTransform: 'uppercase', tracking: '0.05em' }}>
                        {stat.label}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: TESTIMONIOS
        // ==========================================
        if (sec.section_key === 'testimonios') {
          const testimonials = (landingInfo?.testimonials && landingInfo.testimonials.length > 0)
            ? landingInfo.testimonials
            : fallbackTestimonials;

          return (
            <Box key="testimonios" id="testimonios-section" sx={{ py: 12, scrollMarginTop: '88px' }}>
              <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: primaryColor, textTransform: 'uppercase', tracking: '0.12em', mb: 1.5 }}>
                    Opiniones Reales
                  </Typography>
                  <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.025em', mb: 2 }}>
                    Lo que dicen nuestros clientes
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: '600px', mx: 'auto' }}>
                    Nuestra mayor garantía es la satisfacción y estabilidad de los usuarios que ya navegan con nosotros.
                  </Typography>
                </Box>

                <Grid container spacing={4}>
                  {testimonials.map((t) => (
                    <Grid item xs={12} sm={6} md={4} key={t.id}>
                      <Card sx={{ height: '100%', background: 'rgba(30, 41, 59, 0.2)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '24px', p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            {[...Array(5)].map((_, i) => (
                              <StarIcon key={i} sx={{ color: i < t.rating ? '#fbbf24' : 'rgba(255,255,255,0.1)', fontSize: 18 }} />
                            ))}
                          </Stack>
                          <Typography variant="body2" sx={{ color: '#e5e7eb', lineHeight: 1.6, italic: true, mb: 4 }}>
                            "{t.comment}"
                          </Typography>
                        </Box>
                        
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar src={t.avatar_url} sx={{ width: 44, height: 44, bgcolor: primaryColor, color: '#ffffff' }}>
                            {t.client_name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ffffff' }}>
                              {t.client_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {t.district || 'Cliente Activo'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: PREGUNTAS FRECUENTES (FAQ)
        // ==========================================
        if (sec.section_key === 'faq') {
          const faqs = landingInfo?.faqs || [];
          const activeFaqs = faqs.length > 0 ? faqs : fallbackFaqs;

          return (
            <Box key="faq" id="faq-section" sx={{ py: 12, bgcolor: 'rgba(17, 24, 39, 0.3)', borderTop: '1px solid rgba(255, 255, 255, 0.04)', borderBottom: '1px solid rgba(255, 255, 255, 0.04)', scrollMarginTop: '88px' }}>
              <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1.5 }}>
                    <FaqIcon sx={{ color: primaryColor, fontSize: 22 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: primaryColor, textTransform: 'uppercase', tracking: '0.1em' }}>
                      Preguntas Frecuentes
                    </Typography>
                  </Stack>
                  <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.025em', mb: 2 }}>
                    ¿Tienes dudas? Te ayudamos
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                    Encuentra respuestas rápidas sobre cómo funciona la instalación, la simetría y la autogestión.
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  {activeFaqs.map((faq, idx) => (
                    <Accordion key={faq.id || idx} sx={{ background: 'rgba(30, 41, 59, 0.25)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '16px !important', overflow: 'hidden', boxShadow: 'none', '&:before': { display: 'none' }, '&.Mui-expanded': { borderColor: alpha(primaryColor, 0.35), background: 'rgba(30, 41, 59, 0.4)' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: primaryColor }} />} sx={{ py: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff' }}>
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pb: 3, pt: 0, px: 3 }}>
                        <Typography variant="body2" sx={{ color: '#9ca3af', lineHeight: 1.6 }}>
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Container>
            </Box>
          );
        }

        // ==========================================
        // SECCIÓN: FORMULARIO DE CONTACTO / INFO COMERCIAL
        // ==========================================
        if (sec.section_key === 'contacto') {
          return (
            <Container key="contacto" id="contacto-section" maxWidth="lg" sx={{ py: 12, scrollMarginTop: '88px' }}>
              <Grid container spacing={8} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: secondaryColor, textTransform: 'uppercase', tracking: '0.12em', mb: 2 }}>
                    Atención Inmediata
                  </Typography>
                  <Typography variant="h3" fontWeight={900} gutterBottom sx={{ letterSpacing: '-0.025em', mb: 3 }}>
                    Contacto & Soporte Comercial
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#9ca3af', mb: 5, lineHeight: 1.7 }}>
                    ¿Tienes dudas sobre la cobertura en tu zona o quieres contratar un plan dedicado? Escríbenos o visítanos en nuestros horarios comerciales.
                  </Typography>

                  <Stack spacing={3}>
                    {settings.phone && (
                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(primaryColor, 0.1), color: primaryColor }}><MobileIcon /></Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Teléfono de Atención</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">{settings.phone}</Typography>
                        </Box>
                      </Stack>
                    )}
                    
                    {settings.whatsapp && (
                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981' }}><WifiIcon /></Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">WhatsApp Ventas</Typography>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', color: '#10b981' }} onClick={() => window.open(`https://wa.me/${settings.whatsapp.replace(/\D/g, '')}`, '_blank')}>
                            {settings.whatsapp}
                          </Typography>
                        </Box>
                      </Stack>
                    )}

                    {settings.email && (
                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(secondaryColor, 0.1), color: secondaryColor }}><EmailIcon /></Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Correo Corporativo</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">{settings.email}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {settings.address && (
                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><LocationIcon /></Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Dirección Física</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">{settings.address}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {settings.opening_hours && (
                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar sx={{ bgcolor: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><TimeIcon /></Avatar>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Horarios de Atención</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">{settings.opening_hours}</Typography>
                        </Box>
                      </Stack>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Card sx={{ background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.9) 100%)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '32px', p: { xs: 4, md: 6 }, boxShadow: '0 25px 50px rgba(0,0,0,0.4)' }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#ffffff', mb: 1 }}>
                      Consulta tu Cobertura Online
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                      Ingresa tus datos y un especialista técnico verificará el mapa satelital AP para confirmar factibilidad de instalación.
                    </Typography>

                    <form onSubmit={handleLeadSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Nombre Completo" variant="outlined" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} required sx={darkInputStyle} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Teléfono / celular" variant="outlined" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} required sx={darkInputStyle} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Correo Electrónico" type="email" variant="outlined" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} sx={darkInputStyle} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Distrito" variant="outlined" value={leadForm.district} onChange={(e) => setLeadForm({ ...leadForm, district: e.target.value })} sx={darkInputStyle} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Dirección Exacta" variant="outlined" value={leadForm.address} onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })} sx={darkInputStyle} />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <FormControl fullWidth sx={darkInputStyle}>
                            <InputLabel sx={{ color: '#9ca3af' }}>Plan de Interés</InputLabel>
                            <Select value={leadForm.interest_plan} label="Plan de Interés" onChange={(e) => setLeadForm({ ...leadForm, interest_plan: e.target.value })} sx={{ borderRadius: '12px' }}>
                              <MenuItem value=""><em>Ninguno / No estoy seguro</em></MenuItem>
                              {plans.map(p => (
                                <MenuItem key={p.id || p.speed} value={`${p.speed} Mbps`}>{p.speed} Mbps (S/ {p.price})</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField fullWidth multiline rows={3} label="Observaciones o Detalles Adicionales" value={leadForm.observations} onChange={(e) => setLeadForm({ ...leadForm, observations: e.target.value })} sx={darkInputStyle} />
                        </Grid>

                        {leadAlert.show && (
                          <Grid item xs={12}>
                            <Alert severity={leadAlert.severity}>{leadAlert.message}</Alert>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Button type="submit" variant="contained" fullWidth disabled={leadLoading} sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#ffffff', fontWeight: 'bold', py: 1.8, borderRadius: '12px', textTransform: 'none', fontSize: '1rem', boxShadow: `0 4px 20px ${alpha(primaryColor, 0.3)}` }}>
                            {leadLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Consulta de Cobertura'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          );
        }

        return null;
      })}

      {/* SECCIÓN DE CONTACTO DEL COMERCIO Y ENLACES (REQUISITO CERTIFICACIÓN OPENPAY BBVA) */}
      <Box component="footer" sx={{ bgcolor: '#070a12', borderTop: '1px solid rgba(255, 255, 255, 0.06)', pt: 6, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* 1. Datos de Contacto Oficial del Comercio (Obligatorio BBVA) */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight="800" sx={{ color: '#ffffff', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BankIcon sx={{ color: primaryColor, fontSize: 22 }} />
                {settings.company_name || 'YinoWISP'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2, lineHeight: 1.6 }}>
                Proveedor de Telecomunicaciones e Internet de alta velocidad en Fibra Óptica y Enlaces Inalámbricos.
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <PhoneIcon sx={{ color: secondaryColor, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
                    {settings.company_phone || '+51 924 816 719'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <EmailOutlinedIcon sx={{ color: primaryColor, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                    {settings.company_email || 'soporte@yinowisp.com'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.2}>
                  <LocationIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    {settings.company_address || 'Lima, Perú'} • Atención: Lun a Sáb 8:00 AM - 8:00 PM
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* 2. Accesos Rápidos a Portales */}
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#ffffff', mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Accesos
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: secondaryColor } }} onClick={() => handleClientAccess()}>
                  Portal del Cliente
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: primaryColor } }} onClick={() => navigate('/technician')}>
                  Acceso a Técnicos
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#ffffff' } }} onClick={() => handleAdminAccess()}>
                  Administradores
                </Typography>
              </Stack>
            </Grid>

            {/* 3. Marco Legal y Reclamaciones */}
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#ffffff', mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Legal y Seguridad
              </Typography>
              <Stack spacing={1.5}>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.8, '&:hover': { color: '#60a5fa' } }} 
                  onClick={() => setTermsOpen(true)}
                >
                  <GavelIcon sx={{ fontSize: 16 }} />
                  Términos y Condiciones
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#60a5fa' } }} 
                  onClick={() => setTermsOpen(true)}
                >
                  Políticas de Privacidad (Ley 29733)
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#60a5fa' } }} 
                  onClick={() => setTermsOpen(true)}
                >
                  Política de Cambios y Devoluciones
                </Typography>
              </Stack>
            </Grid>

            {/* 4. Pasarelas de Pago Seguras */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#ffffff', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Pagos 100% Seguros
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                Procesamiento electrónico cifrado con <strong>Openpay (Banco BBVA Perú)</strong> bajo certificación PCI-DSS.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label="Openpay BBVA" size="small" sx={{ bgcolor: 'rgba(0, 68, 129, 0.3)', color: '#60a5fa', fontWeight: 700, border: '1px solid rgba(0, 68, 129, 0.6)' }} />
                <Chip label="Visa / Mastercard" size="small" sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: '#e5e7eb' }} />
                <Chip label="Yape / Plin" size="small" sx={{ bgcolor: 'rgba(115, 34, 130, 0.3)', color: '#d946ef', fontWeight: 700 }} />
                <Chip label="SSL 256-bit" size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontWeight: 700 }} />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.06)', mb: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              &copy; {new Date().getFullYear()} {settings.company_name || 'YinoWISP'}. Todos los derechos reservados.
            </Typography>
            <Typography variant="caption" sx={{ color: '#4b5563' }}>
              Telecomunicaciones y Redes Ópticas de Alta Disponibilidad • Perú
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Modal de Términos y Condiciones Legales */}
      <TermsModal 
        open={termsOpen} 
        onClose={() => setTermsOpen(false)} 
        companyName={settings.company_name || 'YinoWISP'} 
      />

      {/* ==========================================
      MODALES / DIÁLOGOS DE LA LANDING
      ========================================== */}

      {/* Modal Consulta Cobertura Flotante */}
      <Dialog open={coverageOpen} onClose={() => setCoverageOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Solicitar Factibilidad & Cobertura</Typography>
          <IconButton onClick={() => setCoverageOpen(false)} sx={{ color: '#9ca3af' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <form onSubmit={handleLeadSubmit}>
            <Stack spacing={3} sx={{ py: 1 }}>
              <TextField fullWidth label="Nombre Completo" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} required sx={darkInputStyle} />
              <TextField fullWidth label="Número de Teléfono" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} required sx={darkInputStyle} />
              <TextField fullWidth label="Correo Electrónico" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} sx={darkInputStyle} />
              <TextField fullWidth label="Distrito" value={leadForm.district} onChange={(e) => setLeadForm({ ...leadForm, district: e.target.value })} sx={darkInputStyle} />
              <TextField fullWidth label="Dirección de Instalación" value={leadForm.address} onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })} sx={darkInputStyle} />
              
              <FormControl fullWidth sx={darkInputStyle}>
                <InputLabel sx={{ color: '#9ca3af' }}>Plan Seleccionado</InputLabel>
                <Select value={leadForm.interest_plan} label="Plan Seleccionado" onChange={(e) => setLeadForm({ ...leadForm, interest_plan: e.target.value })} sx={{ borderRadius: '12px' }}>
                  <MenuItem value=""><em>Ninguno / No estoy seguro</em></MenuItem>
                  {plans.map(p => (
                    <MenuItem key={p.id || p.speed} value={`${p.speed} Mbps`}>{p.speed} Mbps (S/ {p.price})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField fullWidth multiline rows={2} label="Información adicional (Ej. Altura, referencias)" value={leadForm.observations} onChange={(e) => setLeadForm({ ...leadForm, observations: e.target.value })} sx={darkInputStyle} />
              
              {leadAlert.show && (
                <Alert severity={leadAlert.severity}>{leadAlert.message}</Alert>
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCoverageOpen(false)} sx={{ color: '#9ca3af' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleLeadSubmit} disabled={leadLoading} sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#ffffff', px: 4, py: 1.2, borderRadius: '12px', textTransform: 'none', fontWeight: 'bold' }}>
            {leadLoading ? <CircularProgress size={20} color="inherit" /> : 'Enviar Solicitud'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Popup Promocional de Carga */}
      <Dialog open={popupOpen} onClose={() => setPopupOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: `1px solid ${alpha(primaryColor, 0.2)}`, borderRadius: '32px', p: 1 } }}>
        {activePopup && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase', tracking: '0.05em' }}>¡OFERTA ESPECIAL!</Typography>
              <IconButton onClick={() => setPopupOpen(false)} sx={{ color: '#9ca3af' }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
              {activePopup.image_url && (
                <Box component="img" src={activePopup.image_url} sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '16px', mb: 3 }} />
              )}
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#ffffff' }}>
                {activePopup.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9ca3af', lineHeight: 1.6, mb: 3 }}>
                {activePopup.message}
              </Typography>
              
              {activePopup.button_text && (
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => {
                    setPopupOpen(false);
                    if (activePopup.button_url) {
                      window.open(activePopup.button_url, '_blank');
                    } else {
                      handleOpenCoverage(`Popup: ${activePopup.title}`);
                    }
                  }}
                  sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: '#ffffff', py: 1.5, borderRadius: '12px', fontWeight: 'bold', textTransform: 'none' }}
                >
                  {activePopup.button_text}
                </Button>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
